import type { EquipmentNoteEntry } from '../utils/equipment-notes.util';
import type { EquipmentAssignmentMode } from './equipment-category.constants';

// TelcoTrack — Zařízení (equipment): registr přístrojů s unikátním ID,
// rezervace na dobu, kontroly/servisy a převody mezi sklady.
// Na rozdíl od materiálu se zařízení NEspotřebovává (žádné material_usage) —
// jen se rezervuje, servisuje a přesouvá (AutoCRM WarehouseTransfer / lokální pohyb).

/** Typy servisních událostí zařízení (kontroly a servisy v kalendáři). */
export const EQUIPMENT_SERVICE_EVENT_TYPES = ['kontrola', 'servis', 'revize', 'oprava'] as const;

export type EquipmentServiceEventType = (typeof EQUIPMENT_SERVICE_EVENT_TYPES)[number];

export const EQUIPMENT_SERVICE_EVENT_TYPE_LABELS: Record<EquipmentServiceEventType, string> = {
  kontrola: 'Kontrola',
  servis: 'Servis',
  revize: 'Revize',
  oprava: 'Oprava',
};

export type EquipmentReservationStatus = 'active' | 'cancelled';

export type EquipmentServiceEventStatus = 'planned' | 'done' | 'cancelled';

export const EQUIPMENT_SERVICE_EVENT_STATUS_LABELS: Record<EquipmentServiceEventStatus, string> = {
  planned: 'Naplánováno',
  done: 'Hotovo',
  cancelled: 'Zrušeno',
};

/**
 * Frekvence opakované kontroly (a obdobných servisních událostí).
 * Při vytvoření se materializují konkrétní řádky `equipment_service_events`
 * na horizont {@link EQUIPMENT_SERVICE_RECURRENCE_HORIZON_YEARS} let dopředu.
 */
export const EQUIPMENT_SERVICE_FREQUENCIES = [
  'once',
  'monthly',
  'quarterly',
  'twice_yearly',
  'yearly',
  'every_2_years',
] as const;

export type EquipmentServiceFrequency = (typeof EQUIPMENT_SERVICE_FREQUENCIES)[number];

export const EQUIPMENT_SERVICE_FREQUENCY_LABELS: Record<EquipmentServiceFrequency, string> = {
  once: 'Jednorázově',
  monthly: '1× měsíčně',
  quarterly: '1× čtvrtletně',
  twice_yearly: '2× ročně',
  yearly: '1× ročně',
  every_2_years: '1× za 2 roky',
};

/** Kolik let dopředu se při vytvoření opakované kontroly založí konkrétní termíny. */
export const EQUIPMENT_SERVICE_RECURRENCE_HORIZON_YEARS = 5;

/** Interval ve měsících pro frekvence (once = 0 = jen první termín). */
export const EQUIPMENT_SERVICE_FREQUENCY_MONTHS: Record<EquipmentServiceFrequency, number> = {
  once: 0,
  monthly: 1,
  quarterly: 3,
  twice_yearly: 6,
  yearly: 12,
  every_2_years: 24,
};

/** Aktuální stav zařízení odvozený z rezervací/servisů k danému okamžiku. */
export type EquipmentAvailability = 'available' | 'reserved' | 'in_service' | 'inactive';

export const EQUIPMENT_AVAILABILITY_LABELS: Record<EquipmentAvailability, string> = {
  available: 'Volné',
  reserved: 'Rezervováno',
  in_service: 'V servisu',
  inactive: 'Vyřazeno',
};

// ---------------------------------------------------------------------------
// DTOs (BE ↔ FE přes /api/objects/equipment/*)
// ---------------------------------------------------------------------------

export interface EquipmentDto {
  id: string;
  /** Unikátní ID zařízení (inventární / výrobní číslo) — klíč pro převody. */
  deviceCode: string;
  name: string;
  /** UUID kategorie ze stromu `equipment_category` (Registr / Kategorie). */
  categoryId: string | null;
  /** Zobrazení: breadcrumb cesty kategorie nebo legacy volný text. */
  category: string | null;
  /**
   * Režim přiřazení kusu (`equipment.assignment_mode`).
   * Při vytvoření se předvyplní z kategorie; u zařízení jde upravit.
   */
  assignmentMode?: EquipmentAssignmentMode;
  /** AutoCRM Warehouse.id — vždy povinné (aktuální sklad zařízení). */
  warehouseId: string | null;
  /** AutoCRM Product.id, pokud je zařízení zrcadlené v ERP (převodky přes ERP). */
  warehouseProductId: string | null;
  /** Email držitele (majetková karta); null = na neosobním skladu. */
  assignedUserId?: string | null;
  /** ISO datum posledního podepsaného převzetí. */
  assignedAt?: string | null;
  active: boolean;
  notes: string | null;
  /**
   * Hodnoty vlastních specifikací dle schématu kategorie
   * (`equipment_category.custom_spec_fields`).
   */
  customSpecValues?: Record<string, string | number | boolean | null>;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  /** Odvozený stav k „teď“ (list/summary endpointy). */
  availability?: EquipmentAvailability;
  /** Aktivní rezervace pokrývající „teď“, pokud existuje. */
  currentReservation?: EquipmentReservationDto | null;
}

/** Směr hromadného předávacího protokolu zařízení. */
export const EQUIPMENT_HANDOVER_DIRECTIONS = [
  'handover_to_user',
  'return_from_user',
  'user_to_user',
] as const;

export type EquipmentHandoverDirection = (typeof EQUIPMENT_HANDOVER_DIRECTIONS)[number];

export const EQUIPMENT_HANDOVER_DIRECTION_LABELS: Record<EquipmentHandoverDirection, string> = {
  handover_to_user: 'Předání uživateli',
  return_from_user: 'Vrácení od uživatele',
  user_to_user: 'Převod mezi uživateli',
};

export const EQUIPMENT_HANDOVER_STATUSES = [
  'pending_signature',
  'signed',
  'cancelled',
] as const;

export type EquipmentHandoverStatus = (typeof EQUIPMENT_HANDOVER_STATUSES)[number];

export const EQUIPMENT_HANDOVER_STATUS_LABELS: Record<EquipmentHandoverStatus, string> = {
  pending_signature: 'Čeká na podpis',
  signed: 'Podepsáno',
  cancelled: 'Zrušeno',
};

/** In-app notifikace — čeká podpis předávacího protokolu. */
export const EQUIPMENT_HANDOVER_NOTIFICATION_OBJECT_TYPE = 'EQUIPMENT';
export const EQUIPMENT_HANDOVER_NOTIFICATION_PHASE_SIGN = 'HANDOVER_SIGN';

/** Druhá vratka stejného zařízení, dokud první čeká na přijetí. */
export const EQUIPMENT_RETURN_ALREADY_PENDING_MESSAGE =
  'Vrácení tohoto zařízení už čeká na přijetí.';

export function formatEquipmentHandoverNotificationMessage(params: {
  direction: EquipmentHandoverDirection;
  protocolNumber: string;
  itemCount: number;
  /** Jméno nebo e-mail žadatele. Použije se u vratky. */
  requesterLabel?: string | null;
  /** Názvy vracených zařízení. Jedna notifikace na celou žádost. */
  deviceNames?: string[] | null;
}): string {
  if (params.direction === 'return_from_user') {
    const who = (params.requesterLabel ?? '').trim() || 'Uživatel';
    const names = (params.deviceNames ?? [])
      .map((name) => String(name ?? '').trim())
      .filter(Boolean);
    const device = names.length > 0 ? `zařízení ${names.join(', ')}` : 'zařízení';
    return `${who} chce vrátit ${device}.`;
  }
  const dirLabel = EQUIPMENT_HANDOVER_DIRECTION_LABELS[params.direction] ?? 'Předání zařízení';
  const count = Math.max(0, Math.floor(Number(params.itemCount) || 0));
  const countLabel = count === 1 ? '1 zařízení' : `${count} zařízení`;
  return [
    'Čeká na vás podpis předávacího protokolu zařízení.',
    `${dirLabel} · protokol ${String(params.protocolNumber ?? '').trim() || '—'}`,
    countLabel,
  ].join('\n');
}

export interface EquipmentHandoverItemDto {
  id: string;
  equipmentId: string;
  deviceCode: string;
  name: string;
  category: string;
  warehouseFromId: string | null;
  warehouseToId: string | null;
}

export interface EquipmentHandoverBatchDto {
  id: string;
  protocolNumber: string;
  direction: EquipmentHandoverDirection;
  status: EquipmentHandoverStatus;
  holderUserId: string;
  counterpartyUserId: string | null;
  warehouseFromId: string | null;
  warehouseToId: string | null;
  requiredSignerUserId: string | null;
  note: string | null;
  createdBy: string;
  createdAt: string;
  /** Id uživatele, který přijetí podepsal (`users.id`), nebo e-mail, když účet nejde dohledat. */
  signedBy: string | null;
  /** Jméno uživatele, který přijetí podepsal. */
  signedByName: string | null;
  signedAt: string | null;
  /** U vratky vždy false — držitel přijetí nepodepisuje, podepisuje Zařízení editovat. */
  holderSigned?: boolean;
  pdfDriveFileId: string | null;
  pdfDriveWebUrl: string | null;
  pdfFileName: string | null;
  items: EquipmentHandoverItemDto[];
}

/** Majetková karta uživatele (aktuální držba + protokoly). */
export interface UserAssetCardSummaryDto {
  userEmail: string;
  warehouseId: string | null;
  heldEquipment: EquipmentDto[];
  pendingHandovers: EquipmentHandoverBatchDto[];
  signedProtocols: EquipmentHandoverBatchDto[];
}

export interface EquipmentReservationDto {
  id: string;
  equipmentId: string;
  reservedBy: string;
  purpose: string | null;
  startsAt: string;
  endsAt: string;
  status: EquipmentReservationStatus;
  createdAt: string;
  cancelledAt: string | null;
  cancelledBy: string | null;
}

export interface EquipmentServiceEventDto {
  id: string;
  equipmentId: string;
  eventType: EquipmentServiceEventType;
  title: string | null;
  description: string | null;
  startsAt: string;
  endsAt: string | null;
  status: EquipmentServiceEventStatus;
  createdAt: string;
  createdBy: string | null;
  /** Společné ID série opakovaných termínů; null = jednorázová událost. */
  seriesId: string | null;
  /** Frekvence série (uložena na každé occurrence); null = jednorázově / legacy. */
  frequency: EquipmentServiceFrequency | null;
}

/** Metadata přílohy zařízení (Drive + volitelný legacy download přes GET …/attachments/:id). */
export interface EquipmentAttachmentDto {
  id: string;
  equipmentId: string;
  originalName: string;
  contentType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string;
  driveFileId?: string | null;
  driveWebViewLink?: string | null;
}

/** Detail zařízení (GET /equipment/:id). */
export interface EquipmentDetailDto {
  equipment: EquipmentDto;
  noteEntries: EquipmentNoteEntry[];
  attachments: EquipmentAttachmentDto[];
  /** Složka zařízení na Shared Drive (po prvním uploadu dokumentu). */
  driveFolderId?: string | null;
  driveFolderUrl?: string | null;
  reservations: EquipmentReservationDto[];
  serviceEvents: EquipmentServiceEventDto[];
  transfers: EquipmentTransferDto[];
}

export interface EquipmentTransferDto {
  id: string;
  equipmentId: string;
  deviceCode?: string;
  equipmentName?: string;
  warehouseFromId: string | null;
  warehouseToId: string;
  /** AutoCRM WarehouseTransfer.id, když převod proběhl i v ERP. */
  erpTransferId: string | null;
  note: string | null;
  performedBy: string | null;
  performedAt: string;
}

/** Řádek dokladu příjmu zařízení. */
export interface EquipmentReceiptLineDto {
  id: string;
  equipmentId: string;
  deviceCode: string;
  name: string;
  category: string | null;
  notes: string | null;
}

/** TelcoTrack příjemka zařízení na sklad. */
export interface EquipmentReceiptDocumentDto {
  id: string;
  warehouseId: string;
  description: string | null;
  performedBy: string | null;
  performedAt: string;
  lineCount?: number;
  lines: EquipmentReceiptLineDto[];
}

/** Příloha příjemky zařízení (metadata; soubor v MinIO). */
export interface EquipmentReceiptAttachmentDto {
  id: string;
  receiptId: string;
  originalName: string;
  contentType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string;
}

/** Řádek dokladu výdeje zařízení. */
export interface EquipmentIssueLineDto {
  id: string;
  equipmentId: string;
  deviceCode: string;
  name: string;
  category: string | null;
}

/** TelcoTrack výdejka zařízení ze skladu. */
export interface EquipmentIssueDocumentDto {
  id: string;
  warehouseId: string;
  description: string | null;
  toName: string | null;
  toIco: string | null;
  toAddress: string | null;
  performedBy: string | null;
  performedAt: string;
  lineCount?: number;
  lines: EquipmentIssueLineDto[];
}

/** Počty pro hlavičku sekce Zařízení. */
export interface EquipmentSummaryDto {
  total: number;
  active: number;
  availableNow: number;
  reservedNow: number;
  inServiceNow: number;
}

/** Kalendář obsazenosti jednoho zařízení v období from–to. */
export interface EquipmentCalendarRowDto {
  equipment: EquipmentDto;
  reservations: EquipmentReservationDto[];
  serviceEvents: EquipmentServiceEventDto[];
}

export interface EquipmentCalendarDto {
  from: string;
  to: string;
  rows: EquipmentCalendarRowDto[];
}

// ---------------------------------------------------------------------------
// Overlap logika (čistá — sdílená BE validací i FE kalendářem)
// ---------------------------------------------------------------------------

export interface EquipmentTimeRange {
  /** ISO datetime (včetně). */
  startsAt: string;
  /** ISO datetime (exkluzivní konec; null = otevřený konec, blokuje vše od startu). */
  endsAt: string | null;
}

/**
 * Půlotevřené intervaly [start, end): dotyk konců (end == start) NENÍ konflikt.
 * Otevřený konec (endsAt = null) blokuje vše od svého startu dál.
 */
export function equipmentRangesOverlap(a: EquipmentTimeRange, b: EquipmentTimeRange): boolean {
  const aStart = Date.parse(a.startsAt);
  const bStart = Date.parse(b.startsAt);
  if (!Number.isFinite(aStart) || !Number.isFinite(bStart)) return false;
  const aEnd = a.endsAt == null ? Number.POSITIVE_INFINITY : Date.parse(a.endsAt);
  const bEnd = b.endsAt == null ? Number.POSITIVE_INFINITY : Date.parse(b.endsAt);
  if (!Number.isFinite(aEnd) && a.endsAt != null) return false;
  if (!Number.isFinite(bEnd) && b.endsAt != null) return false;
  return aStart < bEnd && bStart < aEnd;
}

export interface EquipmentScheduleConflict<T extends EquipmentTimeRange = EquipmentTimeRange> {
  kind: 'reservation' | 'service';
  item: T;
}

/**
 * Najde konflikty kandidátní rezervace proti aktivním rezervacím a
 * neukončeným servisním událostem (rezervované/servisované zařízení je
 * v daném období nedostupné).
 */
export function findEquipmentScheduleConflicts(params: {
  candidate: EquipmentTimeRange;
  reservations?: Array<EquipmentReservationDto | (EquipmentTimeRange & { status?: string })>;
  serviceEvents?: Array<EquipmentServiceEventDto | (EquipmentTimeRange & { status?: string })>;
  /** Vlastní id při editaci — přeskočí sám sebe. */
  ignoreReservationId?: string;
}): EquipmentScheduleConflict[] {
  const conflicts: EquipmentScheduleConflict[] = [];
  for (const r of params.reservations ?? []) {
    const status = (r as { status?: string }).status;
    if (status && status !== 'active') continue;
    const id = (r as { id?: string }).id;
    if (params.ignoreReservationId && id === params.ignoreReservationId) continue;
    if (equipmentRangesOverlap(params.candidate, r)) {
      conflicts.push({ kind: 'reservation', item: r });
    }
  }
  for (const e of params.serviceEvents ?? []) {
    const status = (e as { status?: string }).status;
    if (status === 'cancelled') continue;
    if (equipmentRangesOverlap(params.candidate, e)) {
      conflicts.push({ kind: 'service', item: e });
    }
  }
  return conflicts;
}

/** Validace vstupu rezervace: platná data a kladná délka. */
export function validateEquipmentReservationRange(
  startsAt: string,
  endsAt: string,
): string | null {
  const start = Date.parse(startsAt);
  const end = Date.parse(endsAt);
  if (!Number.isFinite(start)) return 'Neplatný začátek rezervace';
  if (!Number.isFinite(end)) return 'Neplatný konec rezervace';
  if (end <= start) return 'Konec rezervace musí být po jejím začátku';
  return null;
}

/** Je zařízení obsazené (rezervace / servis) v okamžiku `at`? */
export function equipmentOccupancyAt(params: {
  at: string | number | Date;
  reservations?: EquipmentReservationDto[];
  serviceEvents?: EquipmentServiceEventDto[];
}): Exclude<EquipmentAvailability, 'inactive'> {
  const t = params.at instanceof Date ? params.at.getTime() : new Date(params.at).getTime();
  if (!Number.isFinite(t)) return 'available';
  const point: EquipmentTimeRange = {
    startsAt: new Date(t).toISOString(),
    endsAt: new Date(t + 1).toISOString(),
  };
  for (const e of params.serviceEvents ?? []) {
    if (e.status === 'cancelled') continue;
    if (equipmentRangesOverlap(point, e)) return 'in_service';
  }
  for (const r of params.reservations ?? []) {
    if (r.status !== 'active') continue;
    if (equipmentRangesOverlap(point, r)) return 'reserved';
  }
  return 'available';
}

// ---------------------------------------------------------------------------
// Opakované kontroly — generování termínů (čistá logika, BE materializuje řádky)
// ---------------------------------------------------------------------------

/** Posun o N měsíců se zachováním dne v měsíci (clamp na poslední den). */
export function addCalendarMonths(date: Date, months: number): Date {
  const result = new Date(date.getTime());
  const day = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDay));
  return result;
}

/**
 * Seznam začátků výskytů včetně kotvy, až po `anchor + horizonYears`.
 * `once` → jen kotva. Bezpečnostní strop 120 výskytů.
 */
export function expandEquipmentServiceOccurrenceStarts(params: {
  anchorStartsAt: string | Date;
  frequency: EquipmentServiceFrequency;
  horizonYears?: number;
}): Date[] {
  const anchor =
    params.anchorStartsAt instanceof Date
      ? new Date(params.anchorStartsAt.getTime())
      : new Date(params.anchorStartsAt);
  if (!Number.isFinite(anchor.getTime())) return [];

  const frequency = params.frequency;
  if (!EQUIPMENT_SERVICE_FREQUENCIES.includes(frequency)) return [anchor];

  const months = EQUIPMENT_SERVICE_FREQUENCY_MONTHS[frequency];
  if (months <= 0) return [anchor];

  const horizonYears = params.horizonYears ?? EQUIPMENT_SERVICE_RECURRENCE_HORIZON_YEARS;
  const horizonEnd = addCalendarMonths(anchor, Math.max(1, horizonYears) * 12);
  const starts: Date[] = [];
  let cursor = anchor;
  const maxOccurrences = 120;
  while (cursor.getTime() < horizonEnd.getTime() && starts.length < maxOccurrences) {
    starts.push(new Date(cursor.getTime()));
    cursor = addCalendarMonths(cursor, months);
  }
  return starts;
}

export function isEquipmentServiceFrequency(value: unknown): value is EquipmentServiceFrequency {
  return (
    typeof value === 'string' &&
    (EQUIPMENT_SERVICE_FREQUENCIES as readonly string[]).includes(value)
  );
}
